/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import uuid from 'uuid/v4';
import Request from '../../utils/Request';
import getConfig from '../../config';
import { getAppLocale } from '@intl/adobe-locales';
import { getLogger } from '../../utils/SearchLogger';
import PerformanceUtils from '../../utils/PerformanceUtils';

export const completionRequest = async ({
	searchQuery,
	searchConfig,
	searchHostContext,
}: {
	searchQuery: string;
	searchConfig: any;
	searchHostContext: HostContext;
}): Promise<unknown> => {
	try {
		const curRequestId = uuid();
		const startTime = Date.now();
		let sanitizeInput = searchQuery;

		if (typeof String.prototype.normalize === 'function') {
			// normalize() exists, use it
			sanitizeInput = sanitizeInput.normalize();
		}

		sanitizeInput = sanitizeInput.trim().toLocaleLowerCase();
		const env = searchHostContext.environment;
		const locale = getAppLocale('uss', searchHostContext.locale) || 'en_US';
		const hostname = getConfig(env, 'USS_SERVER');
		const scope = searchConfig?.ui?.autoComplete?.scope ?? ['cc_search'];
		const path = `/autocomplete/completions?q[text]=${sanitizeInput}&q[locale]=${locale}&scope=${scope}`;
		const timeout = searchConfig?.ui?.autoComplete?.timeout ?? 5000;

		const headers = {
			'Content-Type': 'application/json',
			'x-api-key': searchHostContext.hostInfo.apiKey,
			'x-request-id': curRequestId,
		};

		const autoCompleteResponse = await Request.withTimeout(timeout)
			.withHeaders(headers)
			.send('GET', `https://${hostname}${path}`);

		if (autoCompleteResponse.isError()) {
			const loggerData = {
				url: `https://${hostname}${path}`,
				status: autoCompleteResponse.status(),
				message: autoCompleteResponse.statusText(),
				requestId: curRequestId,
				duration: Date.now() - startTime,
				performanceTiming: PerformanceUtils.getPerformanceTimingByName(autoCompleteResponse.requestUrl()),
			};
			getLogger().error('Search Service', 'Problem with Search Auto Suggestion fetch request', loggerData);
			const autocompleteError = new Error('_performSearchAutoCompleteRequest error');
			return autocompleteError;
		}

		return autoCompleteResponse.data();
	} catch (e) {
		getLogger().error('Autocomplete Service', 'Autocomplete Service connect error', e.toString());
		const autocompleteError = new Error('Autocomplete Service connect error');
		return autocompleteError;
	}
};
